import { Box, Button, Skeleton } from '@mui/material'
import { RoundImage, tokens } from 'ComponentLibrary'
import { TitleImagePlaceholder } from './Placeholder'
import { useRef, type ChangeEvent, type ReactNode, type RefObject } from 'react'

type TitleImageProps = {
    image?: {
        src: string
        alt: string
    }
    fallbackImageSrc?: string[]
    placeholderImageSrc?: string
    uploadImage?: (image: Blob) => void
    isPending?: boolean
    objectFit?: 'cover' | 'contain'
    width?: number
    height?: number
}

const DEFAULT_HEIGHT = 170
const DEFAULT_WIDTH = 225
const ASSET_PLACEHOLDER_IMAGE = '/asset-placeholder.svg'
const ACCEPTED_IMAGE_TYPES = '.png,.jpg,.jpeg'

export default function TitleImage({
    image,
    uploadImage,
    fallbackImageSrc = [],
    placeholderImageSrc = ASSET_PLACEHOLDER_IMAGE,
    isPending = false,
    objectFit = 'cover',
    width = DEFAULT_WIDTH,
    height = DEFAULT_HEIGHT,
}: TitleImageProps) {
    const inputRef = useRef<HTMLInputElement>(null)

    if (isPending) {
        return (
            <Skeleton
                variant="rectangular"
                role="progressbar"
                width={width}
                height={height}
                sx={{ borderRadius: tokens.SizingCornerM, flexShrink: 0 }}
            />
        )
    }

    const imageContent = image ? (
        <ImageFrame width={width} height={height}>
            <RoundImage
                src={image.src}
                alt={image.alt}
                height={height}
                width={width}
                objectFit={objectFit}
                fallbackImageSrc={[...fallbackImageSrc, placeholderImageSrc]}
            />
        </ImageFrame>
    ) : uploadImage ? (
        <TitleImagePlaceholder height={height} width={width} imageSrc={placeholderImageSrc} onClick={() => inputRef.current?.click()} />
    ) : (
        <ImageFrame width={width} height={height}>
            <RoundImage src={placeholderImageSrc} alt="placeholder image" height={height} width={width} objectFit="cover" />
        </ImageFrame>
    )

    if (!uploadImage) {
        return imageContent
    }

    if (!image) {
        return (
            <>
                {imageContent}
                <UploadInput inputRef={inputRef} uploadImage={uploadImage} />
            </>
        )
    }

    return (
        <Button component="label" sx={{ padding: 0, borderRadius: tokens.SizingCornerM }}>
            {imageContent}
            <UploadInput uploadImage={uploadImage} />
        </Button>
    )
}

function UploadInput({ uploadImage, inputRef }: { uploadImage: (image: Blob) => void; inputRef?: RefObject<HTMLInputElement | null> }) {
    const handleImageInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const selectedImage = event.target.files?.[0]
        if (!selectedImage) {
            return
        }

        uploadImage(selectedImage)
        event.target.value = ''
    }

    return <input ref={inputRef} hidden onChange={handleImageInputChange} accept={ACCEPTED_IMAGE_TYPES} type="file" />
}

function ImageFrame({ width, height, children }: { width: number; height: number; children: ReactNode }) {
    return (
        <Box width={width} height={height} borderRadius={tokens.SizingCornerM} overflow="hidden" lineHeight={0} flexShrink={0}>
            {children}
        </Box>
    )
}
